import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowRight, BadgeCheck } from "lucide-react";
import { Breadcrumbs, ButtonLink, Container, Section, H2, Eyebrow } from "../components/Primitives";
import { Fact, Showcase, GemPieceRow, BeadPieceRow, OtherPieceRow, TYPE_SHOP, firstSentences } from "../components/TypeShowcase";
import { Reveal } from "../lib/motion";
import { useCurrency } from "../lib/currency";
import { getProducts } from "../lib/api";
import { useSEO, breadcrumbSchema } from "../lib/seo";
import { grahaBySlug, GRAHA_DETAILS } from "../content/grahas";
import { RASHI_NAV, STONE_DEVA } from "../content/nav";

/**
 * /graha/{slug} — graha detail page, v2.
 *   1. Hero    — full artwork in a framed panel (never crop-zoomed); blurred
 *                art as backdrop; facts as a hairline band under the fold.
 *   2. Reading — the three-paragraph attribution text on an ambient, graha-
 *                coloured ground with the principal-stone CTA beside it.
 *   3. Shop    — one showcase per stone/mukhi type: a single photograph,
 *                convincing (attribution-safe) copy, and every individual
 *                piece as a compact row. No duplicate imagery.
 */
export default function GrahaDetail() {
  const { slug } = useParams();
  const g = grahaBySlug(slug);
  const { format } = useCurrency();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!g) return;
    setLoading(true);
    getProducts({ graha: g.catalog_slug || g.slug, limit: 40 })
      .then((d) => setItems(d.items || []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, [g]);

  const groups = useMemo(() => {
    const gems = new Map();
    const beads = new Map();
    const others = [];
    items.forEach((p) => {
      if (p.type === "gemstone") {
        if (!gems.has(p.stone_slug)) gems.set(p.stone_slug, []);
        gems.get(p.stone_slug).push(p);
      } else if (p.type === "rudraksha") {
        if (!beads.has(p.mukhi)) beads.set(p.mukhi, []);
        beads.get(p.mukhi).push(p);
      } else {
        others.push(p);
      }
    });
    const sortPieces = (arr) => [...arr].sort((a, b) => a.price_inr - b.price_inr);
    return {
      gems: [...gems.entries()]
        .sort(([a], [b]) => (a === g?.stone_slug ? -1 : b === g?.stone_slug ? 1 : 0))
        .map(([k, v]) => [k, sortPieces(v)]),
      beads: [...beads.entries()].sort(([a], [b]) => a - b).map(([k, v]) => [k, sortPieces(v)]),
      others,
    };
  }, [items, g]);

  const fromPrice = useMemo(() => {
    const avail = items.filter((p) => p.stone_slug === g?.stone_slug && p.status === "available");
    return avail.length ? Math.min(...avail.map((p) => p.price_inr)) : null;
  }, [items, g]);

  const trail = g
    ? [
        { label: "Home", to: "/" },
        { label: "Guides", to: "/guide" },
        { label: `${g.graha} — ${g.planet}` },
      ]
    : [];

  useSEO({
    title: g ? `${g.graha} — ${g.planet} · the graha and its principal stones` : "Graha",
    description: g?.lede || "",
    jsonLd: g ? [breadcrumbSchema(trail)] : [],
  });

  if (!g) return <Navigate to="/guide" replace />;

  const rashiLabels = (g.rashis || [])
    .map((s) => RASHI_NAV.find((r) => r.slug === s)?.label || s)
    .join(" · ") || "—";

  return (
    <div className="graha-detail" data-testid={`graha-detail-${g.slug}`}>
      {/* --- 1. Hero — the full artwork, framed. Never crop-zoomed. --- */}
      <div className="graha-hero2 force-dark">
        <div
          className="graha-hero2__bg"
          style={{ backgroundImage: `url(/img/graha/${g.slug}-640.webp)` }}
          aria-hidden="true"
        />
        <div className="graha-hero2__scrim" aria-hidden="true" />
        <Container className="relative z-10 pb-12 pt-10 md:pb-16 md:pt-14">
          <Breadcrumbs trail={trail} />
          <div className="mt-10 grid items-center gap-12 lg:grid-cols-[minmax(0,1fr)_minmax(300px,430px)] lg:gap-16">
            <div className="max-w-[var(--measure-read)] text-white">
              <p className="ui text-[10px] font-medium uppercase tracking-[0.24em] text-[color:#f0d693]">
                The nine grahas · {g.planet}
              </p>
              <p
                className="mt-6 text-6xl leading-none"
                style={{ fontFamily: "'Noto Serif Devanagari', 'Fraunces', serif", color: "#f0d693" }}
              >
                {g.deva}
              </p>
              <h1
                className="mt-3 text-5xl leading-[1.05] tracking-[-0.01em]"
                style={{ fontFamily: "Fraunces, Georgia, serif" }}
              >
                {g.graha}
                <span className="mt-2 block text-2xl font-light italic text-white/70">— {g.planet}</span>
              </h1>
              <p
                className="mt-6 text-[17px] leading-[1.7] text-white/85"
                style={{ fontFamily: "'Source Serif 4', Georgia, serif" }}
              >
                {g.lede}
              </p>
              <div className="mt-8 border-l-2 border-[#f0d693]/50 pl-5" data-testid="graha-hero-mantra">
                <p className="ui text-[10px] font-semibold uppercase tracking-[0.22em] text-[#f0d693]/80">Mantra</p>
                <p className="mt-1.5 text-[20px] text-white" style={{ fontFamily: "'Noto Serif Devanagari', 'Fraunces', serif" }}>
                  {g.mantra}
                </p>
                <p className="ui mt-1 text-[12px] italic text-white/60">{g.mantra_roman}</p>
              </div>
            </div>

            <figure className="graha-hero2__frame" data-testid="graha-hero-art">
              <img
                src={`/img/graha/${g.slug}.webp`}
                alt={`${g.graha} — traditional Pattachitra-style depiction`}
                className="block aspect-square w-full object-cover"
              />
              <figcaption className="ui mt-3 text-center text-[10px] uppercase tracking-[0.22em] text-[#f0d693]/70">
                {g.graha} · Pattachitra-style depiction
              </figcaption>
            </figure>
          </div>

          {/* Facts band — the classical scheme, in a hairline grid. */}
          <div className="graha-hero2__facts mt-12" data-testid="graha-detail-facts">
            <Fact k="Vahana" v={g.vahana} />
            <Fact k="Presiding form" v={g.presiding_form} />
            <Fact k="Day" v={g.day} />
            <Fact k="Direction" v={g.direction} />
            <Fact k="Rashis" v={rashiLabels} wide />
          </div>
        </Container>
      </div>

      {/* --- 2. Reading + principal-stone CTA on an ambient ground. --- */}
      <Section
        flat
        className="graha-knowledge2 py-20 sm:py-24"
        style={{ "--g-accent": g.accent }}
        data-testid="graha-knowledge"
      >
        <div
          className="graha-knowledge2__art"
          style={{ backgroundImage: `url(/img/graha/${g.slug}-640.webp)` }}
          aria-hidden="true"
        />
        <span className="graha-knowledge2__mark" aria-hidden="true">{g.deva}</span>
        <Container className="relative z-10">
          <div className="grid items-start gap-14 lg:grid-cols-[minmax(0,1fr)_380px] lg:gap-20">
            <div>
              <Reveal>
                <Eyebrow>The tradition — worded as attribution</Eyebrow>
                <H2 className="mt-3 max-w-[var(--measure-tight)]">What the classical sources actually say about {g.graha}</H2>
              </Reveal>
              <div className="mt-10 max-w-[var(--measure-wide)] space-y-6">
                {g.knowledge.map((p, idx) => (
                  <p
                    key={idx}
                    className="text-[17px] leading-[1.8] text-ink"
                    style={{ fontFamily: "'Source Serif 4', Georgia, serif" }}
                  >
                    {p}
                  </p>
                ))}
              </div>
            </div>

            <aside className="graha-stone-cta lg:sticky lg:top-28" data-testid="graha-stone-cta">
              <div className="graha-stone-cta__img">
                <img
                  src={`/img/products/${g.stone_slug}-1200.webp`}
                  alt={`${g.stone_name} — the stone read under ${g.graha}`}
                  loading="lazy"
                />
                <span className="ui absolute left-3 top-3 bg-ink/80 px-2.5 py-1 text-[10px] font-medium uppercase tracking-[0.16em] text-wash backdrop-blur">
                  Read under {g.graha}
                </span>
              </div>
              <div className="p-6 sm:p-7">
                <p className="ui text-[10px] font-semibold uppercase tracking-[0.22em] text-gold">
                  The stone of this graha
                </p>
                <h3 className="mt-2.5 text-[26px] leading-tight" style={{ fontFamily: "Fraunces, Georgia, serif" }}>
                  {g.stone_name}
                </h3>
                <p className="mt-1 text-[15px] text-gold">
                  <span className="deva">{STONE_DEVA[g.stone_slug]}</span>
                  <span className="ui ml-2 italic opacity-70">{g.stone_hindi}</span>
                </p>
                <p className="mt-4 text-[14px] leading-[1.7] text-dim"
                   style={{ fontFamily: "'Source Serif 4', Georgia, serif" }}>
                  {g.pitch}
                </p>
                {fromPrice && (
                  <p className="num mt-4 text-[13px] text-ink">
                    Certified pieces from <span className="text-[17px] font-medium">{format(fromPrice)}</span>
                  </p>
                )}
                <ButtonLink
                  to={`/shop/gemstones/${g.stone_slug}`}
                  className="mt-5 w-full justify-center"
                  data-testid="graha-stone-cta-btn"
                >
                  See certified {g.stone_name} <ArrowRight className="ml-1.5 h-4 w-4" />
                </ButtonLink>
                <Link
                  to="/consult/suitability-session"
                  className="ui mt-4 block text-center text-[12px] font-medium uppercase tracking-[0.14em] text-dim underline-offset-4 hover:text-gold hover:underline"
                >
                  Not sure it suits your chart? Book a suitability session
                </Link>
              </div>
            </aside>
          </div>
        </Container>
      </Section>

      {/* --- 3. Shop — one showcase per type, no duplicate photography. --- */}
      <Section className="border-t border-rule py-16 sm:py-20" data-testid="graha-shop">
        <Container>
          <div className="max-w-[var(--measure-wide)]">
            <Eyebrow>Held under {g.graha}</Eyebrow>
            <H2 className="mt-2">The certified pieces we hold under this graha</H2>
            <p className="mt-4 text-[15px] leading-[1.7] text-dim"
               style={{ fontFamily: "'Source Serif 4', Georgia, serif" }}>
              One photograph per type — because our catalogue photography is type-based, not faked
              per stone. Every individual piece below is a separate, one-of-one listing with its own
              origin, treatment, laboratory and report number, verifiable on the laboratory's own website.
            </p>
          </div>

          <div className="mt-12 space-y-10">
            {loading ? (
              [0, 1].map((n) => (
                <div key={n} className="border border-rule bg-surface md:grid md:grid-cols-[340px_1fr]">
                  <div className="skeleton aspect-square" />
                  <div className="space-y-3 p-8">
                    <div className="skeleton h-5 w-1/3" />
                    <div className="skeleton h-4 w-2/3" />
                    <div className="skeleton h-4 w-1/2" />
                  </div>
                </div>
              ))
            ) : items.length === 0 ? (
              <div className="max-w-md border border-rule bg-surface p-8">
                <p className="text-[15px] text-ink">Nothing certified under {g.graha} in stock right now.</p>
                <p className="ui mt-3 text-[12px] text-dim">We only list pieces once the laboratory report is in hand.</p>
                <Link
                  to="/consult/suitability-session"
                  className="mt-5 inline-flex items-center gap-1.5 text-[12px] font-medium uppercase tracking-[0.16em] text-gold hover:underline"
                >
                  Ask us when the next {g.stone_name.toLowerCase()} lands <ArrowRight className="h-3.5 w-3.5" />
                </Link>
              </div>
            ) : (
              <>
                {groups.gems.map(([stoneSlug, pieces]) => (
                  <StoneShowcase key={stoneSlug} g={g} stoneSlug={stoneSlug} pieces={pieces} format={format} />
                ))}
                {groups.beads.map(([mukhi, pieces]) => (
                  <BeadShowcase key={mukhi} g={g} mukhi={mukhi} pieces={pieces} format={format} />
                ))}
                {groups.others.map((p) => (
                  <OtherShowcase key={p.slug} p={p} format={format} />
                ))}
              </>
            )}
          </div>
        </Container>
      </Section>

      {/* --- 4. Why each item is worn — attribution wording. --- */}
      <Section className="py-16" data-testid="graha-why">
        <Container>
          <div className="max-w-[var(--measure-wide)]">
            <Reveal>
              <Eyebrow>Why the tradition places these items under {g.graha}</Eyebrow>
              <H2 className="mt-2">Attribution — never effect</H2>
            </Reveal>
            <p className="mt-6 text-[16px] leading-[1.72] text-ink"
               style={{ fontFamily: "'Source Serif 4', Georgia, serif" }}>
              {g.why_it_is_worn}
            </p>
            <p className="mt-4 text-[15px] italic leading-[1.7] text-dim"
               style={{ fontFamily: "'Source Serif 4', Georgia, serif" }}>
              Traditional attributions on this page are attributions, not effects. We do not say
              a stone brings wealth, cures anxiety or wards off misfortune, because those are
              outcome claims. If you were sold a stone somewhere else and want a second opinion,
              our{" "}
              <Link to="/tools/verify-a-certificate" className="not-italic underline hover:text-ink">
                verify tool
              </Link>{" "}
              will check any seller's report number on the laboratory's own site — we store
              nothing, and you do not have to buy from us.
            </p>
          </div>
        </Container>
      </Section>

      {/* --- Cross-graha nav strip. --- */}
      <Section className="border-t border-rule py-10">
        <Container>
          <p className="eyebrow text-center">Continue through the nine</p>
          <div className="mt-6 flex flex-wrap justify-center gap-2">
            {GRAHA_DETAILS.map((x) => (
              <Link
                key={x.slug}
                to={`/graha/${x.slug}`}
                aria-current={x.slug === g.slug ? "page" : undefined}
                className="graha-dot"
              >
                <span className="graha-dot__deva">{x.deva}</span>
                <span>{x.graha}</span>
              </Link>
            ))}
          </div>
        </Container>
      </Section>
    </div>
  );
}

function StoneShowcase({ g, stoneSlug, pieces, format }) {
  const lead = pieces[0];
  const name = lead.title.replace(/\s*\([^)]*\)\s*$/, "");
  const roman = (lead.title.match(/\(([^)]+)\)/) || [])[1];
  const isPrincipal = stoneSlug === g.stone_slug;
  const copy = isPrincipal
    ? g.pitch
    : `${firstSentences(lead.tradition, 2)} Each piece below is a one-of-one stone — when it sells, it is gone — and its report verifies on the laboratory's own website, not on ours.`;
  return (
    <Showcase
      testId={`graha-showcase-${stoneSlug}`}
      image={`/img/products/${stoneSlug}-1200.webp`}
      alt={`Representative photograph of a certified ${name.toLowerCase()} of this type`}
      chips={[
        { label: "Certified", tone: "border border-verify/60 bg-surface/95 text-verify", icon: <BadgeCheck className="h-3 w-3" /> },
        ...(isPrincipal ? [{ label: `Principal stone of ${g.graha}`, tone: "bg-gold text-white dark:text-[#141310]" }] : []),
      ]}
      eyebrow={`Gemstone · under ${g.graha}`}
      title={name}
      deva={{ text: STONE_DEVA[stoneSlug], roman }}
      copy={copy}
      pieces={pieces}
      renderPiece={(p) => <GemPieceRow key={p.slug} p={p} format={format} />}
      cta={{ to: `/shop/gemstones/${stoneSlug}`, label: `See every ${name.toLowerCase()} in the shop` }}
    />
  );
}

function BeadShowcase({ g, mukhi, pieces, format }) {
  const lead = pieces[0];
  const copy = `${firstSentences(lead.tradition, 2)} Every bead above ₹3,000 is X-rayed before listing, so the chambers you pay for are the chambers inside the seed.`;
  return (
    <Showcase
      testId={`graha-showcase-${mukhi}-mukhi`}
      image={`/img/products/${mukhi}-mukhi-1200.webp`}
      alt={`Representative photograph of a ${mukhi} mukhi rudraksha of this type`}
      chips={[{ label: `Read under ${g.graha}`, tone: "bg-deep text-wash" }]}
      eyebrow={`Rudraksha · under ${g.graha}`}
      title={`${mukhi} Mukhi Rudraksha`}
      deva={{ text: `${mukhi} मुखी रुद्राक्ष`, roman: null }}
      copy={copy}
      pieces={pieces}
      renderPiece={(p) => <BeadPieceRow key={p.slug} p={p} format={format} />}
      cta={{ to: `/shop/rudraksha/${mukhi}-mukhi`, label: `See every ${mukhi} mukhi bead in the shop` }}
    />
  );
}

function OtherShowcase({ p, format }) {
  return (
    <Showcase
      testId={`graha-showcase-${p.slug}`}
      image={p.images?.[0]?.url}
      alt={p.images?.[0]?.alt || p.title}
      chips={[{ label: p.type, tone: "bg-deep text-wash" }]}
      eyebrow={`${p.type} · traditional item`}
      title={p.title}
      deva={null}
      copy={firstSentences(p.description, 2)}
      pieces={[p]}
      renderPiece={(x) => <OtherPieceRow key={x.slug} p={x} format={format} />}
      cta={TYPE_SHOP[p.type] ? { to: TYPE_SHOP[p.type], label: `See every ${p.type} in the shop` } : null}
    />
  );
}
